import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Skeleton } from "@/components/Skeleton";
import { ApexHome } from "@/pages/home/ApexHome";
import { HomeShell } from "@/pages/home/HomeShell";
import { ManagerHome } from "@/pages/home/ManagerHome";
import { MinimalHome } from "@/pages/home/MinimalHome";
import { OperationsHome } from "@/pages/home/OperationsHome";
import { ProducerHome } from "@/pages/home/ProducerHome";
import type { HomeResponse } from "@/pages/home/types";

/** Home, resolved per role.
 *
 *  One request. The server picks the archetype from the viewer's position AND
 *  the people they manage — both live in the org chart and in the visibility
 *  rules in `app/auth/permissions.py`, neither of which `CurrentUser` carries.
 *  The old page fired six requests for every role regardless of relevance,
 *  four of them hard-coded to the viewer's own id, which is why a manager's
 *  Home was a broker's Home with different buttons.
 */
export function Home() {
  const { user } = useAuth();
  const [home, setHome] = useState<HomeResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const allowed = useMemo(
    () => new Set(user?.allowed_pages ?? []),
    [user?.allowed_pages],
  );

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setFailed(false);
    api<HomeResponse>("/api/home")
      .then((r) => alive && setHome(r))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (!user) return null;

  if (failed) {
    return (
      <div className="w-full">
        <div className="card px-5 py-8 text-center max-w-md">
          <div className="text-sm text-slate-200">Couldn't load your home page</div>
          <div className="text-[12.5px] text-slate-500 mt-1">
            Everything else still works — pick a page from the sidebar, or{" "}
            <Link to="/profile" className="text-accent-300 hover:underline">
              open your profile
            </Link>
            .
          </div>
        </div>
      </div>
    );
  }

  if (!home) {
    // The archetype isn't known until the response lands — the cost of letting
    // the server decide. `user.role` picks the skeleton SHAPE only, never data,
    // so a wide-layout role doesn't flash a narrow producer skeleton first.
    const wide = user.role !== "broker" && user.role !== "member";
    return (
      <div className="w-full space-y-5">
        <Skeleton className="h-16 w-72 rounded-xl" />
        {wide ? (
          <>
            <Skeleton className="h-28 w-full rounded-2xl" />
            <div className="grid gap-3 sm:grid-cols-3">
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
            </div>
          </>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-36 rounded-2xl sm:col-span-2" />
            <Skeleton className="h-36 rounded-2xl" />
            <Skeleton className="h-36 rounded-2xl" />
          </div>
        )}
      </div>
    );
  }

  const { data, viewer } = home;

  // Only a producer needs the publish CTAs in the header; a shareholder does
  // not publish and should not be prompted to.
  const producerish = data.archetype === "producer" || data.archetype === "manager";
  const actions = producerish ? (
    <div className="flex items-center gap-2">
      {allowed.has("/publish") && (
        <Link to="/publish" className="btn-primary">
          Publish
        </Link>
      )}
      {allowed.has("/market/publish") && (
        <Link to="/market/publish" className="btn-secondary">
          Bulk publish
        </Link>
      )}
    </div>
  ) : undefined;

  return (
    <HomeShell
      viewer={viewer}
      serverNow={home.server_now}
      archetype={data.archetype}
      allowed={allowed}
      actions={actions}
    >
      {data.archetype === "apex" && (
        <ApexHome data={data} viewer={viewer} allowed={allowed} />
      )}
      {data.archetype === "operations" && (
        <OperationsHome data={data} allowed={allowed} />
      )}
      {data.archetype === "manager" && <ManagerHome data={data} allowed={allowed} />}
      {data.archetype === "producer" && (
        <ProducerHome data={data} allowed={allowed} />
      )}
      {data.archetype === "minimal" && <MinimalHome />}
    </HomeShell>
  );
}
